// Utils and Service
import { getErrorMessage } from '../utils/helpers'
import axios from './instance'

/**
 * Gets the accounts for the specified `clientId`.
 * @param {string} url
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClientAccounts = async (
  url,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(url)

    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Creates a new account with the specified `payload`.
 * @param {string} clientId
 * @param {object} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 */
export const createClientAccount = async (
  clientId,
  payload,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    await axios.post(`/clients/${clientId}/accounts/`, payload)
    setSuccess('New account added.')
    setLoading(false)
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
  }
}

/**
 * Updates the account with the specified `accountId` and `payload`.
 * @param {string} clientId
 * @param {object} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` for the new account
 */
export const updateClientAccount = async (
  clientId,
  payload,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
  onConflict = null,
) => {
  setLoading(true)

  try {
    const response = await axios.patch(`/clients/${clientId}/accounts/${payload.id}/`, payload)
    setSuccess(`${response.name} updated.`)
    setLoading(false)
    return response
  } catch (err) {
    const data = err?.response?.data
    if (err?.response?.status === 409 && data?.code === 'duplicate_account' && onConflict) {
      setLoading(false)
      onConflict(data)
      return null
    }

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the count of portfolio data that would be deleted when deleting an account.
 * @param {string} clientId
 * @param {string} accountId
 * @param {func} setError
 * @param {func} setLoading
 * @returns count of portfolio data records
 */
export const getAccountPortfolioDataCount = async (
  clientId,
  accountId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(
      `/clients/${clientId}/accounts/${accountId}/portfolio-data-count/`,
    )
    setLoading(false)
    return response.count
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return 0
  }
}

/**
 * Deletes the account with the specified `accountId` and `payload`.
 * @param {string} clientId
 * @param {string} accountId
 * @param {object} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 */
export const deleteClientAccount = async (
  clientId,
  accountId,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    await axios.delete(`/clients/${clientId}/accounts/${accountId}/`)
    setLoading(false)
    setSuccess()
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
  }
}

/**
 * Returns counts of how many portfolio data rows would be reassigned vs. how many overlap an
 * existing target row if the source account were merged into the target, plus the overlapping
 * periods (`conflicts`). A non-empty `conflicts` means the merge will be refused.
 */
export const getAccountMergePreview = async (
  clientId,
  sourceId,
  targetId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(
      `/clients/${clientId}/accounts/${sourceId}/merge-preview/?target_id=${targetId}`,
    )
    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Merges the source account into the target account. All of the source's portfolio data rows are
 * reassigned to the target and the source account is deleted on success. If any row would collide
 * with an existing target row (same period), the API refuses the merge with a 409 `merge_conflict`
 * and nothing changes — the overlap must be resolved manually first.
 */
export const mergeClientAccounts = async (
  clientId,
  sourceId,
  targetId,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.post(`/clients/${clientId}/accounts/${sourceId}/merge/`, {
      targetId,
    })
    setLoading(false)
    setSuccess('Accounts merged.')
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}
